import { Box, Grid } from '@mui/material';
import { ReactNode } from 'react';
import theme from '../../../../../Theme';
import useWidth from '../../../../../components/Hooks/useWidth';
import { FeatureFlagEnum } from '../../../../../featureflag/feature-flag.enum';
import { useFeatureFlag } from '../../../../../hooks/useFeatureFlag';
import { UsedInOfflineMode } from '../../../../offline/types/UsedInOfflineMode';
import { Catalog } from '../../../catalog.types';
import CatalogDropIndicator, {
  INDICATOR_THICKNESS,
} from '../../../products/v2/components/CatalogDropIndicator/CatalogDropIndicator';
import { DropPointerSide } from '../../../products/v2/utils/catalogDragAndDrop.types';
import { ConfirmModalState } from '../catalogListPage.types';
import useGetCatalogListColumns from '../hooks/useGetCatalogListColumns';
import { CatalogListDragAndDrop } from '../utils/catalogListDragAndDrop.types';
import CatalogCard, { CatalogCardProps } from './CatalogCard/CatalogCard';
import CatalogListDragAndDropBoundary from './CatalogListDragAndDropBoundary';
import DraggableCatalogCard from './DraggableCatalogCard/DraggableCatalogCard';

interface CatalogListGridSelection {
  isSelectedAll: boolean;
  selectedIds: Set<string>;
  isAnySelected: boolean;
  onToggleSelect: (catalogId: string) => void;
}

interface CatalogListGridProps extends UsedInOfflineMode {
  items: Catalog[];
  readMode: boolean;
  selection: CatalogListGridSelection;
  loadMoreSentinel: ReactNode;
  dragAndDrop: CatalogListDragAndDrop;
  setItem: (catalog: Catalog) => void;
  setConfirmModal: (state: ConfirmModalState) => void;
  onCatalogUpdated: (catalog: Catalog) => void;
}

const CatalogListGrid = ({
  items,
  readMode,
  isOfflineMode = false,
  selection,
  loadMoreSentinel,
  dragAndDrop,
  setItem,
  setConfirmModal,
  onCatalogUpdated,
}: CatalogListGridProps) => {
  const { getFeatureFlag } = useFeatureFlag();
  const enableDynamicCatalog = getFeatureFlag(FeatureFlagEnum.ENABLE_DYNAMIC_CATALOG);
  const columns = useGetCatalogListColumns();
  const { activeBreakpoint } = useWidth();

  const isCatalogSelected = (catalogId: string) => {
    return selection.isSelectedAll || selection.selectedIds.has(catalogId);
  };

  const resolveDropIndicatorSide = (catalogId: string): DropPointerSide | null => {
    const { dropIndicator } = dragAndDrop;

    if (!dropIndicator || dropIndicator.overId !== catalogId) {
      return null;
    }

    return dropIndicator.side;
  };

  const buildCatalogCardProps = (catalog: Catalog): CatalogCardProps => ({
    catalog,
    read: readMode,
    isOfflineMode,
    setItem,
    setConfirmModal,
    isSelected: isCatalogSelected(catalog.id),
    onToggleSelect: () => selection.onToggleSelect(catalog.id),
    isAnySelected: selection.isAnySelected,
    onCatalogUpdated,
  });

  const renderCard = (catalog: Catalog) => {
    const catalogCard = buildCatalogCardProps(catalog);

    if (!dragAndDrop.isEnabled) {
      return <CatalogCard {...catalogCard} />;
    }

    return (
      <DraggableCatalogCard
        catalogCard={catalogCard}
        drag={{
          activeId: dragAndDrop.activeItem?.id ?? null,
          isDraggingDisabled: dragAndDrop.isDraggingDisabled,
          data: { imageUrl: catalog.imageUrl },
        }}
      />
    );
  };

  const grid = (
    <Grid container spacing={2} sx={{ alignItems: 'stretch', paddingTop: theme.spacing(1.5) }}>
      {items.map((catalog: Catalog) => {
        if (catalog.isDynamic && !enableDynamicCatalog) {
          return null;
        }

        const indicatorSide = resolveDropIndicatorSide(catalog.id);

        return (
          <Grid
            key={catalog.id}
            size={{ [activeBreakpoint]: 12 / columns }}
            sx={{ position: 'relative' }}
          >
            {indicatorSide && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  zIndex: 1,
                  width: INDICATOR_THICKNESS,
                  ...(indicatorSide === 'before' ? { left: 0 } : { right: 0 }),
                }}
              >
                <CatalogDropIndicator orientation="vertical" />
              </Box>
            )}

            {renderCard(catalog)}
          </Grid>
        );
      })}
      {loadMoreSentinel}
    </Grid>
  );

  return (
    <CatalogListDragAndDropBoundary dragAndDrop={dragAndDrop}>
      {grid}
    </CatalogListDragAndDropBoundary>
  );
};

export default CatalogListGrid;
